import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useContent } from "../context/ContentContext";
import { loadContent, saveContent } from "../utils/ContentWrapper";


// Fetch image files from public/data/images/images.json
function useImageFiles() {
  const [imageFiles, setImageFiles] = useState([]);
  useEffect(() => {
    fetch("/images/images.json")
      .then((res) => res.ok ? res.json() : [])
      .then((arr) => setImageFiles(Array.isArray(arr) ? arr : []))
      .catch(() => setImageFiles([]));
  }, []);
  return imageFiles;
}


export default function AdminPage() {
  const content = useContent(); // context content wrapper instance
  const [data, setData] = useState(null);
  const [password, setPassword] = useState("");
  const [auth, setAuth] = useState(false);
  const imageFiles = useImageFiles();

  useEffect(() => {
    loadContent()
      .then((json) => {
        if (Object.keys(json).length === 0) {
          setData(content.content);
        } else {
          setData(json);
        }
      })
      .catch(() => {
        setData(content.content);
      });
  }, [content]);

  const stripImagePrefix = (img) =>
    typeof img === "string" ? img.replace(/^src\/data\/images\//, "") : img;

  const processDataForSave = (obj) => {
    if (!obj || typeof obj !== "object") return obj;
    const newObj = Array.isArray(obj) ? [] : {};
    for (const key in obj) {
      if (["logo", "image", "photo"].includes(key)) {
        newObj[key] = stripImagePrefix(obj[key]);
      } else if (typeof obj[key] === "object") {
        newObj[key] = processDataForSave(obj[key]);
      } else {
        newObj[key] = obj[key];
      }
    }
    return newObj;
  };

  const save = async () => {
    const processed = processDataForSave(data);
    try {
      await saveContent(processed);
      alert("Saved to backend!");
    } catch (e) {
      alert("Failed to save to backend");
    }
  };

  if (!auth) {
    return (
      <Box sx={{ maxWidth: 420, mx: "auto", mt: 6 }}>
        <Typography variant="h6">Admin Login</Typography>
        <TextField
          fullWidth
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          sx={{ mt: 2 }}
        />
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => setAuth(password === "admin123")}
        >
          Login
        </Button>
        {password && password !== "admin123" && (
          <Typography color="error" sx={{ mt: 1 }}>
            Incorrect password
          </Typography>
        )}
      </Box>
    );
  }

  if (!data) return <Typography sx={{ mt: 4 }}>Loading content...</Typography>;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Admin Panel
      </Typography>

      {/* Company Info */}
      <Typography variant="h6">Company</Typography>
      <TextField
        fullWidth
        label="Company Name"
        value={data.company.name}
        onChange={(e) =>
          setData({ ...data, company: { ...data.company, name: e.target.value } })
        }
        sx={{ mt: 2 }}
      />
      <TextField
        fullWidth
        label="Tagline"
        value={data.company.tagline}
        onChange={(e) =>
          setData({ ...data, company: { ...data.company, tagline: e.target.value } })
        }
        sx={{ mt: 2 }}
      />
      <TextField
        fullWidth
        label="Address"
        value={data.company.address || ""}
        onChange={(e) =>
          setData({ ...data, company: { ...data.company, address: e.target.value } })
        }
        sx={{ mt: 2 }}
      />
      <TextField
        fullWidth
        label="City"
        value={data.company.city || ""}
        onChange={(e) =>
          setData({ ...data, company: { ...data.company, city: e.target.value } })
        }
        sx={{ mt: 2 }}
      />
      <TextField
        fullWidth
        label="Email"
        value={data.company.email || ""}
        onChange={(e) =>
          setData({ ...data, company: { ...data.company, email: e.target.value } })
        }
        sx={{ mt: 2 }}
      />
      <TextField
        fullWidth
        label="Phones (comma separated)"
        value={(data.company.phones || []).join(", ")}
        onChange={(e) =>
          setData({
            ...data,
            company: {
              ...data.company,
              phones: e.target.value
                .split(",")
                .map((p) => p.trim())
                .filter(Boolean),
            },
          })
        }
        sx={{ mt: 2 }}
      />

      {/* Logo Selection */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1">Company Logo</Typography>
        {data.company.logo && (
          <Box sx={{ mb: 1 }}>
            <img
              src={content.resolveImage(data.company.logo)}
              alt="Logo"
              style={{ maxHeight: 80, maxWidth: 200 }}
            />
          </Box>
        )}
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel>Select Logo</InputLabel>
          <Select
            value={data.company.logo || ""}
            label="Select Logo"
            onChange={(e) =>
              setData({ ...data, company: { ...data.company, logo: e.target.value } })
            }
          >
            <MenuItem value="">None</MenuItem>
            {imageFiles.map((img) => (
              <MenuItem key={img} value={img}>
                {img}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Add more sections below like Hero, Services, Team, Portfolio... (same structure) */}
       {/* Hero Section */}
      <Typography variant="h6">Hero Slides</Typography>
      <Button
        variant="outlined"
        onClick={() =>
          setData({
            ...data,
            hero: [...(data.hero || []), { text: "New Slide", image: "" }],
          })
        }
        sx={{ mt: 1, mb: 1 }}
      >
        Add Slide
      </Button>

      <Grid container spacing={2}>
        {(data.hero || []).map((h, i) => (
          <Grid item xs={12} md={6} key={i}>
            <Card>
              {h.image && (
                <CardMedia
                  component="img"
                  height="180"
                  image={content.resolveImage(h.image)}
                  alt={h.text}
                />
              )}
              <CardContent>
                <TextField
                  fullWidth
                  label="Text"
                  value={h.text}
                  onChange={(e) => {
                    const newHero = [...data.hero];
                    newHero[i].text = e.target.value;
                    setData({ ...data, hero: newHero });
                  }}
                  sx={{ mb: 1 }}
                />
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputLabel id={`hero-image-select-label-${i}`}>Select Image</InputLabel>
                  <Select
                    labelId={`hero-image-select-label-${i}`}
                    value={h.image || ""}
                    label="Select Image"
                    onChange={e => {
                      const newHero = [...data.hero];
                      newHero[i].image = e.target.value;
                      setData({ ...data, hero: newHero });
                    }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {imageFiles.map((img) => (
                      <MenuItem key={img} value={img}>{img}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  color="error"
                  variant="outlined"
                  sx={{ mt: 1 }}
                  onClick={() => {
                    const newHero = [...data.hero];
                    newHero.splice(i, 1);
                    setData({ ...data, hero: newHero });
                  }}
                >
                  Remove Slide
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Mission & Vision */}
      <Typography variant="h6">Mission & Vision</Typography>
      <TextField
        fullWidth
        multiline
        label="Mission"
        value={data.mission}
        onChange={(e) => setData({ ...data, mission: e.target.value })}
        sx={{ mt: 1 }}
      />
      <TextField
        fullWidth
        multiline
        label="Vision"
        value={data.vision}
        onChange={(e) => setData({ ...data, vision: e.target.value })}
        sx={{ mt: 1 }}
      />

      <Divider sx={{ my: 2 }} />

      {/* Services */}
      <Typography variant="h6">Services</Typography>
      <Button
        variant="outlined"
        onClick={() => setData({
          ...data,
          services: [
            ...(data.services || []),
            { title: "New Service", description: "", image: "" }
          ]
        })}
        sx={{ mt: 1, mb: 1 }}
      >
        Add Service
      </Button>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {data.services.map((s, i) => (
          <Grid item xs={12} md={6} key={i}>
            <Card>
              {s.image && (
                <CardMedia
                  component="img"
                  height="140"
                  image={content.resolveImage(s.image)}
                  alt={s.title}
                />
              )}
              <CardContent>
                <TextField
                  fullWidth
                  label="Title"
                  value={s.title}
                  onChange={(e) => {
                    const newServices = [...data.services];
                    newServices[i].title = e.target.value;
                    setData({ ...data, services: newServices });
                  }}
                  sx={{ mb: 1 }}
                />
                <TextField
                  fullWidth
                  label="Description"
                  value={s.description}
                  onChange={(e) => {
                    const newServices = [...data.services];
                    newServices[i].description = e.target.value;
                    setData({ ...data, services: newServices });
                  }}
                  sx={{ mb: 1 }}
                />
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputLabel id={`service-image-select-label-${i}`}>Select Image</InputLabel>
                  <Select
                    labelId={`service-image-select-label-${i}`}
                    value={s.image || ""}
                    label="Select Image"
                    onChange={e => {
                      const newServices = [...data.services];
                      newServices[i].image = e.target.value;
                      setData({ ...data, services: newServices });
                    }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {imageFiles.map((img) => (
                      <MenuItem key={img} value={img}>{img}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  color="error"
                  variant="outlined"
                  sx={{ mt: 1 }}
                  onClick={() => {
                    const newServices = [...data.services];
                    newServices.splice(i, 1);
                    setData({ ...data, services: newServices });
                  }}
                >
                  Remove Service
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Team */}
      <Typography variant="h6">Team</Typography>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {data.team.map((m, i) => (
          <Grid item xs={12} md={4} key={i}>
            <Card sx={{ p: 2 }}>
              {m.photo && (
                <CardMedia
                  component="img"
                  height="140"
                  image={content.resolveImage(m.photo)}
                  alt={m.name}
                />
              )}
              <CardContent>
                <TextField
                  fullWidth
                  label="Name"
                  value={m.name}
                  onChange={(e) => {
                    const newTeam = [...data.team];
                    newTeam[i].name = e.target.value;
                    setData({ ...data, team: newTeam });
                  }}
                  sx={{ mb: 1 }}
                />
                <TextField
                  fullWidth
                  label="Title"
                  value={m.title}
                  onChange={(e) => {
                    const newTeam = [...data.team];
                    newTeam[i].title = e.target.value;
                    setData({ ...data, team: newTeam });
                  }}
                  sx={{ mb: 1 }}
                />
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputLabel id={`team-photo-select-label-${i}`}>Select Photo</InputLabel>
                  <Select
                    labelId={`team-photo-select-label-${i}`}
                    value={m.photo || ""}
                    label="Select Photo"
                    onChange={e => {
                      const newTeam = [...data.team];
                      newTeam[i].photo = e.target.value;
                      setData({ ...data, team: newTeam });
                    }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {imageFiles.map((img) => (
                      <MenuItem key={img} value={img}>{img}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  color="error"
                  variant="outlined"
                  sx={{ mt: 1 }}
                  onClick={() => {
                    const newTeam = [...data.team];
                    newTeam.splice(i, 1);
                    setData({ ...data, team: newTeam });
                  }}
                >
                  Remove Member
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Portfolio */}
      <Typography variant="h6">Portfolio</Typography>
      <Button
        variant="outlined"
        onClick={() =>
          setData({
            ...data,
            portfolio: [
              ...(data.portfolio || []),
              { title: "New project", image: "" },
            ],
          })
        }
        sx={{ mt: 1, mb: 1 }}
      >
        Add Item
      </Button>
      <Grid container spacing={2}>
        {(data.portfolio || []).map((p, i) => (
          <Grid item xs={12} md={4} key={i}>
            <Card>
              {p.image && (
                <CardMedia
                  component="img"
                  height="140"
                  image={content.resolveImage(p.image)}
                  alt={p.title}
                />
              )}
              <CardContent>
                <TextField
                  fullWidth
                  label="Title"
                  value={p.title || ""}
                  onChange={(e) => {
                    const newPortfolio = [...(data.portfolio || [])];
                    newPortfolio[i].title = e.target.value;
                    setData({ ...data, portfolio: newPortfolio });
                  }}
                  sx={{ mb: 1 }}
                />
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputLabel id={`portfolio-image-select-label-${i}`}>Select Image</InputLabel>
                  <Select
                    labelId={`portfolio-image-select-label-${i}`}
                    value={p.image || ""}
                    label="Select Image"
                    onChange={e => {
                      const newPortfolio = [...(data.portfolio || [])];
                      newPortfolio[i].image = e.target.value;
                      setData({ ...data, portfolio: newPortfolio });
                    }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {imageFiles.map((img) => (
                      <MenuItem key={img} value={img}>{img}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  color="error"
                  variant="outlined"
                  sx={{ mt: 1 }}
                  onClick={() => {
                    const newPortfolio = [...(data.portfolio || [])];
                    newPortfolio.splice(i, 1);
                    setData({ ...data, portfolio: newPortfolio });
                  }}
                >
                  Remove Item
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Divider sx={{ my: 3 }} />

      {/* Save Button */}
      <Box sx={{ mt: 3 }}>
        <Button variant="contained" onClick={save}>
          Save All
        </Button>
      </Box>
    </Box>
  );
}
